import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Crown, Dumbbell, Flame, House, Library, MonitorPlay, TrendingUp, User, Utensils } from 'lucide-react';

import { GET_ME } from '../../graphql/queries';
import { ThemeToggle } from './ThemeToggle';
import logo from '../../../assets/plates/header-logo.png';
import { prefersReducedMotion } from '../../utils/motion';
import { TAB_NAVIGATION } from '../../utils/scrollMemory';
import { useAppOnPhone } from '../../utils/useAppOnPhone';
import { useTabBarDrag } from '../../hooks/useTabBarDrag';

// Lucide's house filled in, with the door cut out: the active Início tab
const HouseFilled = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 21v-8a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v8z"
    />
  </svg>
);

// Phones get five tabs, the calorie tracker last: their account is the avatar in the header. The desktop rail, with the
// room, adds Progresso, the Central de Aprendizado, Planos and the account (its avatar); the tracker sits under Dieta
const TABS = [
  { key: 'home', to: '/dashboard', Icon: House, ActiveIcon: HouseFilled },
  { key: 'workouts', to: '/workouts', Icon: Dumbbell },
  { key: 'exercises', to: '/exercises', Icon: Library },
  { key: 'progress', to: '/progress', Icon: TrendingUp, railOnly: true },
  { key: 'diet', to: '/diet', Icon: Utensils },
  { key: 'calories', to: '/calorie-tracker', Icon: Flame },
  { key: 'academy', to: '/academy', Icon: MonitorPlay, railOnly: true },
  { key: 'plans', to: '/plans', Icon: Crown, railOnly: true },
  { key: 'account', to: '/account', Icon: User, railOnly: true },
];

// Pages without a tab of their own light up the section they belong to (exact first path segment); on phones that
// includes the rail-only ones (Progresso is a shortcut on Início)
const PARENT_TAB = {
  workout: 'workouts',
  'split-review': 'workouts',
  plans: 'account',
  progress: 'home',
  academy: 'home',
  sync: 'home',
};

// Ignore tiny scroll jitter before switching state
const SCROLL_THRESHOLD = 12;

/**
 * railOnDesktop: from 960px the pill becomes a 72px icon rail on the page ground, after Instagram's: the logo on top,
 * the nine tabs centered in the viewport with the avatar standing in for the Conta icon, the theme switch at the
 * foot. Hover or keyboard focus opens it over the page with labels; from 1680px it stays open.
 */
export const FloatingTabBar = ({ railOnDesktop = false }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // The rail's breakpoint (the CSS turns the pill into the rail at the same width)
  const isRail = useMediaQuery({ query: '(min-width: 960px)' }) && railOnDesktop;
  // Inside the installed app on a phone, Início is the home at /
  const appOnPhone = useAppOnPhone();
  const tabs = TABS.filter((tab) => isRail || !tab.railOnly).map((tab) => (appOnPhone && tab.key === 'home' ? { ...tab, to: '/' } : tab));
  const segment = pathname.split('/')[1];
  const parentTab = tabs.some((tab) => tab.to === `/${segment}`) ? null : PARENT_TAB[segment];
  const navigate = useNavigate();
  const keys = isRail ? [] : tabs.map((tab) => tab.key);
  const exact = tabs.find((tab) => tab.to === `/${segment}`);
  const current = isRail ? null : (exact?.key ?? parentTab ?? null);
  const { barRef, capsuleRef, select } = useTabBarDrag({
    keys,
    current,
    reduced: prefersReducedMotion(),
    onSelect: (key) => navigate(tabs.find((tab) => tab.key === key).to, { state: TAB_NAVIGATION }),
  });
  const { data } = useQuery(GET_ME);
  const me = data?.me;
  const isPro = me?.plan === 'pro' && ['active', 'trialing'].includes(me?.subscriptionStatus);
  const initial = (me?.name || '?').trim().charAt(0).toUpperCase();
  const [isCompact, setIsCompact] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    lastY.current = window.scrollY;

    const handleScroll = () => {
      // iOS reports its rubber band too. Past the page's ends nobody is scrolling, the page is only bouncing back: read
      // as a scroll up, that grew the bar mid-bounce, at the end of every short page
      const end = document.documentElement.scrollHeight - window.innerHeight;
      const y = Math.max(0, Math.min(window.scrollY, Math.max(end, 0)));
      const delta = y - lastY.current;
      if (Math.abs(delta) < SCROLL_THRESHOLD) return;
      // Compact while scrolling down, full again when scrolling up or back near the top
      setIsCompact(delta > 0 && y > 80);
      lastY.current = y;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      ref={barRef}
      data-lit={isRail ? undefined : (current ?? '')}
      className={`mt-root mt-tabbar${railOnDesktop ? ' mt-tabbar--rail-desktop' : ''}${isCompact ? ' mt-tabbar--compact' : ''}`}
      aria-label={t('pages.Dashboard.home.tabs.label')}
    >
      <div ref={capsuleRef} className="mt-tabbar__capsule" aria-hidden="true" data-shown="false" />
      {railOnDesktop && (
        <Link className="mt-tabbar__brand" to="/dashboard">
          <img src={logo} alt="" />
          <span>{t('appName')}</span>
        </Link>
      )}
      {tabs.map(({ key, to, Icon, ActiveIcon = Icon }) => (
        <NavLink
          key={key}
          to={to}
          state={TAB_NAVIGATION}
          end={to === '/'}
          // The tab of the page already open takes it back to the top, as on a phone's own apps
          data-tab={key}
          draggable={false}
          onClick={(e) => {
            if (pathname === to) {
              window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
              return;
            }
            // Phone pill: the capsule glides there first, then the page changes
            if (!isRail && !(e.metaKey || e.ctrlKey || e.shiftKey || e.button)) {
              e.preventDefault();
              select(key);
            }
          }}
          className={({ isActive }) => `mt-tab mt-tab--${key}${isActive || parentTab === key ? ' mt-tab--active' : ''}`}
        >
          {({ isActive: isExact }) => {
            const isActive = isExact || parentTab === key;
            return (
            <>
              {isActive ? (
                <ActiveIcon className="mt-tab__icon" aria-hidden="true" />
              ) : (
                <Icon className="mt-tab__icon" aria-hidden="true" />
              )}
              {/* Desktop only: the avatar stands in for the Conta icon, as Instagram's Perfil does; phones keep the icon */}
              {railOnDesktop && key === 'account' && (
                <span className="mt-tab__avatar" aria-hidden="true">
                  {initial}
                  {isPro && <span className="mt-tab__avatar-pro">{t('pages.Dashboard.proPlan')}</span>}
                </span>
              )}
              <span className="mt-tab__label">{t(`pages.Dashboard.home.tabs.${key}`)}</span>
            </>
            );
          }}
        </NavLink>
      ))}
      {railOnDesktop && (
        <div className="mt-tabbar__foot">
          <ThemeToggle className="mt-tabbar__theme" withLabel />
        </div>
      )}
    </nav>
  );
};
